import ClipLogoUrl from '@assets/images/partners/clip.png';
import LoyverseLogoUrl from '@assets/images/partners/loyverse.png';
import MercadoPagoLogoUrl from '@assets/images/partners/mercadoPago.png';
import PickerLogoUrl from '@assets/images/partners/picker.png';
import StripePagoLogoUrl from '@assets/images/partners/stripe.png';
import { IntegrationCard, IntegrationDetail, IntegrationSection } from '@interfaces/Integration';
import { IntegrationConfiguration, ModuleName } from '@utils/constants';
import { hasFeatureFlag } from '@utils/hooks/useGetFeatureFlags';
import {
  IconBrandWhatsappFilled,
  IconBrandMeta,
  IconBrandGoogleAnalytics,
  IconBrandGoogleMaps,
  IconBrandTiktok,
} from '@tabler/icons-react';
import { WhatsappQrModal } from './components/providers/whatsapp/WhatsappQrModal';
import { useWhatsappConnection } from './components/providers/whatsapp/hooks/useWhatsappConnection';
import { useMetaCloudConnection } from './components/providers/meta-cloud-api/hooks/useMetaCloudConnection';
import { useGoogleBusinessConnection } from './components/providers/google-business/hooks/useGoogleBusinessConnection';
import { GoogleBusinessConnectModal } from './components/providers/google-business/GoogleBusinessConnectModal';

// ... (existing code)

// const jelpIntegration: IntegrationCard = {
//   provider_code: 'jelp',
//   provider_type: 'delivery',
//   logo_url: JelpLogoUrl,
//   available: true,
//   description: 'Automatiza las operaciones de entrega de tus pedidos recibidos en Plick.',
//   website: 'https://jelp.app/',
//   help_link: 'https://wa.me/5216647773919',
//   disclaimer:
//     'Para activar este servicio debes tener activo Envío a domicilio. Ingresa los siguientes datos de tu API Key de tu cuenta [Jelp Delivery](https://jelp.app/).',
//   notes: [
//     '* Pagos de tarifas y comisiones de este servicio se realizan directamente con la empresa Jelp Delivery',
//     '* Al activar este servicio estas de acuerdo en que una empresa ajena a Plick realice las entregas, por lo que **Plick no se hace responsable de cualquier evento o incidencia ocurrida en la entrega**.',
//   ],
//   configuration: {
//     branch_code: { key: 'branch_code', label: 'Código de sucursal', default: '' },
//     sign: { key: 'sign', label: 'Sign', default: '' },
//     api_key: { key: 'api_key', label: 'Api key', default: '' },
//   },
//   actions: ['connect', 'disconnect', 'detail', 'configuration'],
// };

const pickerIntegration: IntegrationCard = {
  provider_code: 'picker',
  name: 'Picker',
  provider_type: 'delivery',
  logo_url: PickerLogoUrl,
  available: true,
  description: 'Automatiza las operaciones de entrega de tus pedidos recibidos en Plick.',
  website: 'https://dashboard.pickerexpress.com/',
  disclaimer:
    'Para activar este servicio debes tener activo Envío a domicilio. Ingresa los siguientes datos de tu API Key de tu cuenta [Picker Express](https://dashboard.pickerexpress.com/settings/general).',
  notes: [
    '* Pagos de tarifas y comisiones de este servicio se realizan directamente con la empresa Picker Express',
    '* Al activar este servicio estas de acuerdo en que una empresa ajena a Plick realice las entregas, por lo que **Plick no se hace responsable de cualquier evento o incidencia ocurrida en la entrega**.',
  ],
  configuration: {
    token: { key: 'token', label: 'Token', default: '', fieldType: 'private' },
  },
  actions: ['connect', 'disconnect', 'sync', 'go_to_provider_dashboard'],
};

const loyverseIntegration: IntegrationCard = {
  provider_code: 'loyverse',
  name: 'Loyverse',
  provider_type: 'tpv',
  logo_url: LoyverseLogoUrl,
  available: true,
  description: 'Conecta y sincroniza tu menú Plick con el punto de venta y gestión de inventario.',
  website: 'https://loyverse.com/',
  disclaimer: 'Ingresa los siguientes datos para conectar tu cuenta de Loyverse con tu menú digital Plick.',
  notes: [
    '* Al dar clic en Guardar tu menú actual se eliminará y se copiará el que tengas creado en tu cuenta de Loyverse.',
    '* El menú solo podrá ser administrado desde tu cuenta Loyverse.',
    '* Podrás sincronizarlos cada que lo desees.',
  ],
  configuration: {
    store_id: { key: 'store_id', label: 'ID de sucursal', default: '' },
    token: { key: 'token', label: 'Token', default: '' },
  },
  actions: ['connect', 'disconnect', 'configuration'],
};

// const softRestaurantIntegration: IntegrationCard = {
//   provider_code: 'softRestaurant',
//   provider_type: 'rms',
//   logo_url: SoftRestauranteLogoUrl,
//   available: true,
//   description: 'Integra tu menú Plick con Soft Restaurant para la gestión de tu restaurante.',
//   website: 'https://softrestaurant.com/',
//   disclaimer: 'Ingresa los siguientes datos para conectar tu cuenta de Softrestaurant con tu menú digital Plick.',
//   notes: [
//     '* Al dar clic en Guardar tu menú actual se eliminará y se copiará el que tengas creado en tu cuenta de Loyverse.',
//     '* El menú solo podrá ser administrado desde tu cuenta Loyverse.',
//     '* Podrás sincronizarlos cada que lo desees.',
//   ],
//   configuration: {
//     api_key: { key: 'api_key', label: 'Api key', default: '' },
//   },
//   actions: ['connect', 'disconnect', 'sync'],
// };

const stripeIntegration: IntegrationCard = {
  provider_code: 'stripe',
  name: 'Stripe',
  provider_type: 'payment',
  logo_url: StripePagoLogoUrl,
  helpVideoUrl: 'https://youtu.be/7J8Z8r6VQSw',
  helpVideoTitle: 'Tutorial: Stripe',
  helpVideoDescription: 'Aprende cómo conectar tu cuenta de Stripe con tu menú digital Plick.',
  available: true,
  description: 'Agiliza los pagos en tu menú Plick con Stripe: seguridad y eficiencia en cada transacción..',
  website: 'https://stripe.com/en-mx',
  disclaimer:
    'Ingresa los siguientes datos para conectar tu cuenta de Stripe con tu menú digital Plick. Los puedes conseguir [haciendo click aquí](https://dashboard.stripe.com/account/apikeys).',
  configuration: {
    public_key: { key: 'public_key', label: 'Clave publicable', default: '', fieldType: 'public' },
    secret_key: { key: 'secret_key', label: 'Clave secreta', default: '', fieldType: 'private' },
  },
  actions: ['connect', 'disconnect'],
};

const clipIntegration: IntegrationCard = {
  provider_code: 'clip',
  name: 'Clip',
  provider_type: 'payment',
  logo_url: ClipLogoUrl,
  available: true,
  description: 'Integra Clip a tu sistema para ofrecer una solución de pago ágil y segura.',
  website: 'https://www.clip.mx/',
  disclaimer:
    'Ingresa los siguientes datos para conectar tu cuenta de Clip con tu menú digital Plick. Los puedes conseguir [haciendo click aquí](https://dashboard.developer.clip.mx/applications).',
  configuration: {
    public_key: { key: 'public_key', label: 'API Key', default: '', fieldType: 'public' },
    secret_key: { key: 'secret_key', label: 'Clave secreta', default: '', fieldType: 'private' },
  },
  actions: ['connect', 'disconnect'],
};

const mercadoPagoIntegration: IntegrationCard = {
  provider_code: 'mercadoPago',
  name: 'Mercado Pago',
  provider_type: 'payment',
  logo_url: MercadoPagoLogoUrl,
  helpVideoUrl: 'https://youtu.be/jc2CK5Z6XoA',
  helpVideoTitle: 'Tutorial: Mercado Pago',
  helpVideoDescription: 'Aprende cómo conectar tu cuenta de Mercado Pago con tu menú digital Plick.',
  available: true,
  description: 'Conecta tu menú Plick con Mercado Pago y transforma la manera en que procesas los pagos.',
  website: 'https://www.mercadopago.com.mx/',
  configuration: {
    public_key: { key: 'public_key', label: 'Public key', default: '', fieldType: 'public' },
    secret_key: { key: 'secret_key', label: 'Access token', default: '', fieldType: 'private' },
  },
  actions: ['connect', 'disconnect'],
};

// const netPayIntegration: IntegrationCard = {
//   provider_code: 'netpay',
//   provider_type: 'payment',
//   logo_url: NetPayLogoUrl,
//   available: hasFeatureFlag('netpay-register'),
//   description: 'Integra tu menú Plick con NetPay para un procesamiento de pagos en línea rápido y seguro.',
//   website: 'https://netpay.mx/',
//   configuration: {
//     api_key: { key: 'api_key', label: 'Api key', default: '' },
//   },
//   actions: ['connect', 'disconnect', 'sync'],
// };

const whatsappBusinessIntegration: IntegrationCard = {
  provider_code: 'whatsappBusiness',
  name: 'WhatsApp Business',
  provider_type: 'communication',
  logo_url: IconBrandWhatsappFilled,
  helpVideoUrl: 'https://youtu.be/1wtjblaFd3A',
  helpVideoTitle: 'Tutorial: WhatsApp Business',
  helpVideoDescription: 'Aprende cómo conectar tu cuenta de WhatsApp Business con tu menú digital Plick.',
  available: true,
  description:
    'Automatiza la comunicación con tus clientes. Mantén informados a tus clientes con el estatus de su pedido en tiempo real.',
  website: 'https://www.whatsapp.com/',
  configuration: {}, // Self-service: conexión vía QR, no requiere configuración manual
  logoClassName: 'bg-color-whatsapp_dark text-color-white',

  // Custom upsell modal
  upsellTitle: 'Integración con WhatsApp Business',
  upsellDescription:
    'La integración envía automáticamente un mensaje personalizado cada vez que cambia el estatus de un pedido.\n\nAdapta cada mensaje al tono de voz de tu marca, agrega links a tus redes sociales, incluye cupones para la próxima compra o incluso una encuesta rápida para conocer la experiencia de tus clientes.\n\n**Métodos de entrega soportados:**\nEnvío a domicilio, Pasar a Recoger y Comer en Restaurante.',
  upsellCtaLabel: 'Más Información',
  upsellCtaUrl:
    'https://api.whatsapp.com/send?phone=+529935318237&text=Me%20interesa%20la%20Integraci%C3%B3n%20de%20WhatsApp%20Business.',

  // Nuevas acciones basadas en composition pattern
  actions: ['qr_connect', 'show_status', 'configuration'],

  // Tipo de conexión
  connectionType: 'qr',

  // Hook personalizado para conexión
  useConnectionHook: useWhatsappConnection,

  // Modal de conexión QR
  ConnectionModal: WhatsappQrModal,
};

const metaCloudApiIntegration: IntegrationCard = {
  provider_code: 'metaCloudApi',
  name: 'WhatsApp Cloud API',
  provider_type: 'communication',
  logo_url: IconBrandWhatsappFilled,
  available: true,
  description:
    'Conecta tu WhatsApp Business oficial vía Meta Cloud API para notificaciones automatizadas con tu número verificado.',
  website: 'https://business.whatsapp.com/',
  configuration: {},
  logoClassName: 'bg-[#0866FF] text-color-white',

  upsellTitle: 'WhatsApp Cloud API (Meta)',
  upsellDescription:
    'Integración oficial con la API Cloud de Meta. Usa tu número de WhatsApp Business verificado para enviar notificaciones automáticas a tus clientes con cada cambio de estatus.',
  upsellCtaLabel: 'Más Información',
  upsellCtaUrl:
    'https://api.whatsapp.com/send?phone=+529935318237&text=Me%20interesa%20la%20Integraci%C3%B3n%20de%20WhatsApp%20Cloud%20API.',

  actions: ['oauth_connect', 'show_status', 'configuration'],
  connectionType: 'oauth',
  useConnectionHook: useMetaCloudConnection,
};

const kapsoIntegration: IntegrationCard = {
  provider_code: 'kapso',
  name: 'Kapso',
  provider_type: 'communication',
  logo_url: IconBrandWhatsappFilled,
  available: true,
  description:
    'Conecta Kapso para enviar notificaciones automáticas a tus clientes vía WhatsApp con cada cambio de estatus.',
  website: 'https://kapso.io/',
  logoClassName: 'bg-[#25D366] text-color-white',
  disclaimer:
    'Ingresa los datos de tu cuenta de Kapso para conectar la integración. Puedes encontrar estas credenciales en tu panel de [Kapso](https://kapso.io/).',

  upsellTitle: 'Kapso — WhatsApp Notifications',
  upsellDescription: 'Integra Kapso para enviar notificaciones automáticas de estatus de pedido vía WhatsApp.',
  upsellCtaLabel: 'Más Información',
  upsellCtaUrl:
    'https://api.whatsapp.com/send?phone=+529935318237&text=Me%20interesa%20la%20Integraci%C3%B3n%20de%20Kapso.',

  configuration: {
    business_account_id: { key: 'business_account_id', label: 'Business Account ID', default: '' },
    phone_number_id: { key: 'phone_number_id', label: 'Phone Number ID', default: '' },
    kapso_api_key: { key: 'kapso_api_key', label: 'Api Key', default: '', fieldType: 'private' },
  },
  actions: ['connect', 'disconnect', 'configuration'],
  connectionType: 'form',
  connectViaSync: true,
};

// ── Marketing (tracking de conversiones) ──
// La config vive en CommerceIntegration (provider_type 'marketing'); el emisor
// server-side (plick-gateway-conversions) usa meta_capi para enviar cada
// Purchase a Meta, y el menú público proyecta public_measurement_id de ga4
// para cargar gtag del comercio. Ver ARD en plick_menu_v2.1/docs/arquitectura.
const metaCapiIntegration: IntegrationCard = {
  provider_code: 'meta_capi',
  name: 'Meta CAPI',
  provider_type: 'marketing',
  logo_url: IconBrandMeta,
  logoClassName: 'bg-[#0668E1] text-color-white',
  available: true,
  description:
    'Registra tus ventas directo desde el servidor de Plick (Conversions API): se miden aunque haya bloqueadores o el cliente cierre la página.',
  website: 'https://www.facebook.com/business/help/2041148702652965',
  disclaimer:
    'FUENTE ÚNICA del Pixel: este ID alimenta el pixel del navegador Y las conversiones server-side. El token es opcional — sin él, el pixel funciona normal en el navegador; agrégalo cuando quieras activar Conversions API. Se genera en tu [Business Manager](https://business.facebook.com/) → Events Manager → tu pixel → Configuración → Conversions API → Generar token.',
  configuration: {
    public_pixel_id: {
      key: 'public_pixel_id',
      label: 'ID del Pixel de Facebook',
      default: '',
      fieldType: 'public',
      // Solo dígitos: pegarlo con espacios o con texto alrededor dejaba el pixel
      // inválido sin ningún aviso.
      pattern: /^\d{5,20}$/,
      patternHint: 'El ID del Pixel son solo números (normalmente 15-16 dígitos).',
    },
    access_token: {
      key: 'access_token',
      label: 'Token de Conversions API (opcional)',
      default: '',
      fieldType: 'private',
      optional: true,
    },
  },
  // Prellenado la primera vez desde el pixel legacy de Comercio (migración suave
  // hacia la fuente única sin tocar a quienes ya lo usan).
  prefillFromCommerce: { public_pixel_id: 'pixel_id' },
  actions: ['connect', 'disconnect'],
  // Marketing es el único provider_type cuyo PUT hace merge server-side: se puede
  // agregar el token después de conectar solo-pixel, corregir el pixel o quitar el
  // token sin pasar por Desconectar (DELETE destructivo).
  supportsEdit: true,
};

const tiktokIntegration: IntegrationCard = {
  provider_code: 'tiktok_pixel',
  name: 'TikTok Pixel',
  provider_type: 'marketing',
  logo_url: IconBrandTiktok,
  logoClassName: 'bg-color-black text-color-white',
  available: true,
  description:
    'Registra tus ventas directo desde el servidor de Plick (Events API): se miden aunque haya bloqueadores o el cliente cierre la página.',
  website: 'https://ads.tiktok.com/help/article/events-api',
  disclaimer:
    'FUENTE ÚNICA del Pixel: este código alimenta el pixel del navegador Y las conversiones server-side. El token es opcional — sin él, el pixel funciona normal en el navegador; agrégalo cuando quieras activar Events API. Se genera en tu [TikTok Events Manager](https://www.tiktok.com/business/events-manager) → tu pixel → Configuración → Events API → Generar Access Token.',
  configuration: {
    public_pixel_code: {
      key: 'public_pixel_code',
      label: 'ID del Pixel de TikTok',
      default: '',
      fieldType: 'public',
      // TikTok usa un id alfanumérico (p. ej. C1A2B3...): se acota a alfanumérico
      // para atrapar espacios/pegados con texto.
      pattern: /^[A-Za-z0-9]{10,40}$/,
      patternHint: 'El ID del Pixel de TikTok es alfanumérico, sin espacios ni guiones.',
    },
    access_token: {
      key: 'access_token',
      label: 'Token de Events API (opcional)',
      default: '',
      fieldType: 'private',
      optional: true,
    },
  },
  actions: ['connect', 'disconnect'],
  supportsEdit: true,
};

const ga4Integration: IntegrationCard = {
  provider_code: 'ga4',
  name: 'Google Analytics 4',
  provider_type: 'marketing',
  logo_url: IconBrandGoogleAnalytics,
  logoClassName: 'bg-[#F9AB00] text-color-white',
  available: true,
  description: 'Mide tu menú y tus conversiones en tu propia cuenta de Google Analytics 4 (y Google Ads).',
  website: 'https://analytics.google.com/',
  disclaimer:
    'Pega el Measurement ID de tu propiedad GA4 (formato G-XXXXXXXXXX). Lo encuentras en Administrar → Flujos de datos → tu flujo web.',
  configuration: {
    public_measurement_id: {
      key: 'public_measurement_id',
      label: 'Measurement ID (G-XXXXXXXXXX)',
      default: '',
      fieldType: 'public',
      // Formato oficial G-XXXXXXXXXX. Copiarlo de la consola de GA suele arrastrar
      // un espacio al final → gtag no medía nada y no había error visible.
      pattern: /^G-[A-Z0-9]{4,}$/,
      patternHint: 'El Measurement ID tiene el formato G-XXXXXXXXXX (mayúsculas y números).',
    },
  },
  actions: ['connect', 'disconnect'],
  supportsEdit: true,
};

const googleBusinessIntegration: IntegrationCard = {
  provider_code: 'google_business',
  name: 'Perfil de Empresa en Google',
  provider_type: 'marketing',
  logo_url: IconBrandGoogleMaps,
  logoClassName: 'bg-[#4285F4] text-color-white',
  available: true,
  description:
    'Administra tu ficha de Google Maps desde Plick: datos del negocio, reseñas, estadísticas y el botón de pedidos.',
  website: 'https://business.google.com/',
  disclaimer:
    'Busca tu negocio como aparece en Google Maps y selecciónalo. No necesitas la contraseña de la cuenta de Google que creó la ficha.',
  notes: [
    '* Al vincular tu ficha podrás ver tu calificación y tus reseñas dentro de Plick.',
    '* Para responder reseñas, publicar novedades y apuntar el botón "Pedir en línea" a tu menú Plick, tu ficha debe quedar administrada por Plick. Te ayudamos con ese paso.',
  ],

  upsellTitle: 'Perfil de Empresa en Google',
  upsellDescription:
    'Tu ficha de Google es donde la mayoría de tus clientes te encuentra. Conéctala a Plick para ver tus reseñas y estadísticas en un solo lugar, responder sin salir del panel y mandar el botón "Pedir en línea" directo a tu menú Plick — sin comisiones de intermediarios.',
  upsellCtaLabel: 'Más Información',
  upsellCtaUrl:
    'https://api.whatsapp.com/send?phone=+529935318237&text=Me%20interesa%20la%20Integraci%C3%B3n%20con%20Google%20Business%20Profile.',

  // La ficha se elige buscándola en Google, no capturando credenciales.
  configuration: {},
  actions: ['connect', 'disconnect', 'show_status', 'configuration'],
  connectionType: 'oauth',
  useConnectionHook: useGoogleBusinessConnection,
  // Sin esto, "Conectar" llamaba a connect() y no pasaba NADA visible: la
  // vinculación necesita que el comercio elija su ficha, y ese paso vive aquí.
  ConnectionModal: GoogleBusinessConnectModal,
};

export const integrationSections: IntegrationSection[] = [
  {
    title: 'Notificaciones a clientes',
    integrations: [
      whatsappBusinessIntegration,
      ...(hasFeatureFlag('meta-cloud-api') ? [metaCloudApiIntegration] : []),
      ...(hasFeatureFlag('kapso') ? [kapsoIntegration] : []),
    ],
    moduleName: ModuleName.NOTIFICACIONES_CLIENTE_WHATSAPP,
  },
  {
    title: 'Servicios de repartidores',
    integrations: [pickerIntegration], // Jelp temporalmente oculto
    moduleName: ModuleName.PROVEEDORES_ENTREGA,
  },
  {
    title: 'Puntos de venta',
    integrations: [loyverseIntegration],
    moduleName: ModuleName.LOYVERSE, // Cambio específico para Loyverse
  },
  {
    title: 'Gestion de restaurante',
    integrations: [
      /*softRestaurantIntegration*/
    ], // SoftRestaurant temporalmente oculto
  },
  {
    title: 'Metodos de pago',
    integrations: [stripeIntegration, clipIntegration, mercadoPagoIntegration], // NetPay temporalmente oculto
  },
  {
    title: 'Marketing',
    integrations: [metaCapiIntegration, tiktokIntegration, ga4Integration],
    moduleName: ModuleName.SEGUIMIENTO_REDES_SOCIALES,
  },
  {
    // Sección propia y no dentro de "Marketing": la ficha de Google no es tracking,
    // es el canal donde el cliente descubre al negocio y decide si pide.
    title: 'Presencia en Google',
    integrations: [...(hasFeatureFlag('google-business-profile') ? [googleBusinessIntegration] : [])],
    // Módulo POR PROVEEDOR, como LOYVERSE — no por categoría. Sin esto la sección
    // queda sin gate (`hasProviderIntegration` cae a `true`), los botones nacen
    // habilitados para todos y el upsell declarado arriba es inalcanzable.
    moduleName: ModuleName.GOOGLE_BUSINESS,
  },
];

export const commerceIntegrations: IntegrationCard[] = integrationSections.flatMap((section) => section.integrations);

export const defaultIntegrationConfig: IntegrationConfiguration = {
  token: '',
  sign: '',
  branch_code: '',
  api_key: '',
  // Kapso
  kapso_api_key: '',
  phone_number_id: '',
  business_account_id: '',
  // Marketing — los campos públicos (public_pixel_id/public_pixel_code/
  // public_measurement_id) NO se siembran aquí a propósito: con un '' inicial el
  // `formValue ?? savedValue` del Modal nunca caería al valor guardado y el campo
  // saldría vacío en edición (los secretos de pago sí conservan justamente porque
  // no están sembrados). Al omitirlos, `data[key]` es undefined y el `??` sí
  // muestra el valor guardado, igual que en pagos. El access_token va aparte: se
  // deja vacío en edición vía has_token (rama isSecuredToken en el Modal).
  access_token: '',
};

const jelpDataBalanceCode = 'usableBalance';
export const jelpIntegrationDetail: IntegrationDetail = {
  provider_code: 'jelp',
  provider_type: 'delivery',
  availableData: [],
  cardAvailableData: [
    {
      code: jelpDataBalanceCode,
      label: 'Saldo Disponible',
      icon: 'ion:wallet',
    },
  ],
  configuration: {
    fields: [],
  },
};

export const commerceIntegrationDetails: IntegrationDetail[] = [jelpIntegrationDetail];
export const integrationDetailsMap = commerceIntegrationDetails.reduce(
  (map, detail) => {
    map[detail.provider_code] = detail;
    return map;
  },
  {} as { [key: string]: IntegrationDetail },
);

export interface IntegrationError {
  errorCode: string;
  message: string;
  requestId: string;
}

export interface ProviderData {
  [key: string]: string | number | boolean | object;
}

export interface ProviderConfigurationPayload<T> {
  providerData: T;
}

export type MissingValuesChecker<T> = (registerData: T) => (keyof T)[];
